'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { company, addressLines } from '@/lib/company';

// The registered office is in Bradford, Ontario (America/Toronto). A live
// local clock is a small "real place, real company" signal — and here it is
// also a factual one: it is the time where the corporation is registered.
function useOfficeTime() {
  const [time, setTime] = useState('');
  useEffect(() => {
    const fmt = () =>
      new Intl.DateTimeFormat('en-CA', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: company.timeZone,
        hour12: false,
      }).format(new Date());
    const tick = () => setTime(fmt());
    // Defer the first set out of the effect body (avoids a synchronous
    // setState-in-effect) and skip any SSR/client time mismatch.
    const raf = requestAnimationFrame(tick);
    const id = setInterval(tick, 15_000);
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(id);
    };
  }, []);
  return time;
}

const companyLinks = [
  { name: 'About DilaCode', href: '/about-dilacode' },
  { name: 'Company details', href: '/company' },
  { name: 'Selected work', href: '/portfolio' },
  { name: 'Notes', href: '/articles' },
  { name: 'Work with us', href: '/apply' },
];

const productLinks = [
  { name: 'DilaCRM', href: '/products/dilacrm' },
  { name: 'DilaERP', href: '/products/dilaerp' },
  { name: 'dila.menu', href: '/products/dilamenu' },
  { name: 'All products', href: '/products' },
];

const capabilityLinks = [
  { name: 'AI integration', href: '/services/ai-integration' },
  { name: 'Web development', href: '/services/web-design-development' },
  { name: 'Mobile development', href: '/services/mobile-app-development' },
  { name: 'UI/UX design', href: '/services/ui-ux-design' },
  { name: 'All capabilities', href: '/services' },
];

const legalLinks = [
  { name: 'Support', href: '/support' },
  { name: 'Terms', href: '/terms' },
  { name: 'Privacy', href: '/privacy-policy' },
];

function FooterLink({ href, children, external = false }: {
  href: string;
  children: React.ReactNode;
  external?: boolean;
}) {
  return (
    <Link
      href={href}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      className="group relative w-fit text-sm font-light text-white/55 transition-colors duration-300 hover:text-white"
    >
      <span className="relative">
        {children}
        <span className="absolute -bottom-0.5 left-0 h-px w-full origin-right scale-x-0 bg-current transition-transform duration-300 group-hover:origin-left group-hover:scale-x-100" />
      </span>
    </Link>
  );
}

export function Footer() {
  const currentYear = new Date().getFullYear();
  const officeTime = useOfficeTime();

  return (
    <footer
      className="self-styled relative overflow-hidden border-t border-white/10 bg-black"
      // Keep the last row clear of the iPhone home indicator (viewport-fit=cover).
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="content-padding">
        {/* ── Top: pitch + link columns ── */}
        <div className="grid grid-cols-1 gap-16 pb-20 pt-24 md:pb-24 md:pt-32 lg:grid-cols-[1.2fr_2fr] lg:gap-24">
          {/* Pitch + primary CTA */}
          <div className="flex flex-col gap-6">
            <span className="eyebrow">Start a conversation</span>
            <p className="max-w-sm text-2xl font-light leading-snug text-white md:text-3xl">
              Have something to build?
              <br />
              <span className="text-white/50">Tell us what you need.</span>
            </p>
            <a
              href={`mailto:${company.email}`}
              className="group flex w-fit items-center gap-3 text-base font-medium text-white md:text-lg"
            >
              <span className="relative">
                {company.email}
                <span className="absolute -bottom-1 left-0 h-px w-full origin-left bg-[var(--color-accent)] transition-transform duration-300 group-hover:scale-x-110" />
              </span>
              <span
                aria-hidden="true"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-black transition-transform duration-500 group-hover:rotate-45"
                style={{ backgroundColor: 'var(--color-accent)' }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="7" y1="17" x2="17" y2="7" />
                  <polyline points="7 7 17 7 17 17" />
                </svg>
              </span>
            </a>
          </div>

          {/* Link columns */}
          <div className="grid grid-cols-2 gap-x-8 gap-y-12 sm:grid-cols-3 md:gap-x-12 lg:grid-cols-4">
            <nav aria-label="Company" className="flex flex-col gap-5">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                Company
              </span>
              {/* inline gap: the un-layered global `nav ul { gap: 40px }`
                  (header nav) would override the Tailwind utility */}
              <ul className="flex flex-col" style={{ gap: '0.625rem' }}>
                {companyLinks.map((link) => (
                  <li key={link.name} className="flex">
                    <FooterLink href={link.href}>{link.name}</FooterLink>
                  </li>
                ))}
              </ul>
            </nav>

            <nav aria-label="Products" className="flex flex-col gap-5">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                Products
              </span>
              <ul className="flex flex-col" style={{ gap: '0.625rem' }}>
                {productLinks.map((link) => (
                  <li key={link.name} className="flex">
                    <FooterLink href={link.href}>{link.name}</FooterLink>
                  </li>
                ))}
              </ul>
            </nav>

            <nav aria-label="Capabilities" className="flex flex-col gap-5">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                Capabilities
              </span>
              <ul className="flex flex-col" style={{ gap: '0.625rem' }}>
                {capabilityLinks.map((link) => (
                  <li key={link.name} className="flex">
                    <FooterLink href={link.href}>{link.name}</FooterLink>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="col-span-2 flex flex-col gap-5 sm:col-span-1">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                Registered office
              </span>
              <ul className="flex flex-col gap-2.5">
                <li className="flex">
                  <FooterLink href={`mailto:${company.email}`}>{company.email}</FooterLink>
                </li>
                <li className="flex">
                  <FooterLink href={`tel:${company.telHref}`}>{company.phone}</FooterLink>
                </li>
                <li>
                  <address className="text-sm font-light not-italic leading-relaxed text-white/55">
                    {addressLines[0]}
                    <br />
                    {addressLines[1]}
                  </address>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* ── Corporate details strip — the verifiable facts, in plain sight ── */}
        <div className="flex flex-col gap-3 border-t border-white/[0.07] py-7 md:flex-row md:items-baseline md:justify-between">
          <p className="font-mono text-[11px] uppercase leading-relaxed tracking-[0.18em] text-white/45">
            {company.legalName}
            <span className="mx-2 text-white/25">/</span>
            Ontario corporation No.&nbsp;{company.corporationNumber}
            <span className="mx-2 text-white/25">/</span>
            Incorporated in {company.incorporatedIn}
          </p>
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/45">
            Contact: {company.contactName}
          </p>
        </div>

        {/* ── Bottom bar ── */}
        <div className="flex flex-col gap-6 border-t border-white/[0.07] py-9 md:flex-row md:items-center md:justify-between md:py-10">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <span className="text-[13px] font-light text-white/55">
              &copy; {currentYear} {company.legalName}
            </span>
            {legalLinks.map((link) => (
              <Link
                key={link.name}
                href={link.href}
                className="text-[13px] font-light text-white/55 transition-colors hover:text-white"
              >
                {link.name}
              </Link>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {/* Live office clock */}
            <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-white/55">
              <span
                aria-hidden="true"
                className="inline-block h-1.5 w-1.5 rounded-full animate-pulse"
                style={{ backgroundColor: 'var(--color-accent)' }}
              />
              {company.timeZoneLabel} {officeTime || '--:--'}
            </span>

            {/* Back to top */}
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="group flex cursor-pointer items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.24em] text-white/55 transition-colors hover:text-white"
            >
              Top
              <svg
                className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Ghost wordmark — bleeds off the bottom edge */}
      <div aria-hidden="true" className="pointer-events-none select-none">
        <div
          className="font-six-caps -mb-[5vw] mt-8 text-center text-[17.5vw] leading-[0.72] text-transparent md:mt-12"
          style={{ WebkitTextStroke: '1px rgba(255,255,255,0.06)' }}
        >
          {company.wordmark}
        </div>
      </div>
    </footer>
  );
}
